<template>
  <div
    v-if="effectiveCount > 0"
    class="scroll-indicator-overlay d-flex justify-content-center align-items-center"
  >
    <div class="scroll-indicator-badge shadow-sm">
      <slot :count="effectiveCount">~{{ effectiveCount }}</slot>
      <font-awesome-icon icon="computer-mouse-scrollwheel" class="ms-2" />
      <font-awesome-icon icon="angles-down" class="ms-2" />
    </div>
  </div>
  <div v-if="effectiveCount > 0" class="scroll-bottom-shadow"></div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, toRef, watch } from "vue";
import { useEventListener } from "@vueuse/core";

/** Min scroll distance (px) before the indicator shows — only used with scrollTarget. */
const SCROLL_DISTANCE_THRESHOLD_PX = 20;

const props = withDefaults(
  defineProps<{
    /** Pass a scrollable element to let the component auto-track remaining items. */
    scrollTarget?: HTMLElement | null;
    /** Approximate row height in px — only used with scrollTarget. */
    itemHeight?: number;
    /** Manual override: supply a pre-computed count instead of auto-tracking. */
    remainingCount?: number;
  }>(),
  {
    scrollTarget: null,
    itemHeight: 42,
    remainingCount: undefined,
  }
);

const autoCount = ref(0);

const safeItemHeight = computed(() =>
  props.itemHeight > 0 ? props.itemHeight : 42
);

const effectiveCount = computed(() => props.remainingCount ?? autoCount.value);

const updateScrollState = (target: HTMLElement) => {
  const distance = target.scrollHeight - target.scrollTop - target.clientHeight;
  autoCount.value =
    distance > SCROLL_DISTANCE_THRESHOLD_PX
      ? Math.max(0, Math.ceil(distance / safeItemHeight.value))
      : 0;
};

const scrollTargetRef = toRef(props, "scrollTarget");

useEventListener(scrollTargetRef, "scroll", (event) => {
  updateScrollState(event.target as HTMLElement);
});

let mutationObserver: MutationObserver | null = null;

watch(
  scrollTargetRef,
  (el, oldEl) => {
    if (oldEl) {
      mutationObserver?.disconnect();
    }
    if (el) {
      updateScrollState(el);
      if (!mutationObserver) {
        mutationObserver = new MutationObserver(() => {
          if (scrollTargetRef.value) {
            updateScrollState(scrollTargetRef.value);
          }
        });
      }
      mutationObserver.observe(el, { childList: true, subtree: true });
    } else {
      autoCount.value = 0;
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  mutationObserver?.disconnect();
  mutationObserver = null;
});
</script>

<style scoped lang="scss">
.scroll-indicator-overlay {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  z-index: 5;
  pointer-events: none;
}

.scroll-indicator-badge {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  border: 1px solid var(--phoenix-border-color);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--phoenix-gray-700);
  display: flex;
  align-items: center;
}

.scroll-bottom-shadow {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1));
  pointer-events: none;
  z-index: 4;
  border-radius: 0 0 6px 6px;
}
</style>
